import {createSelector} from 'reselect'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  useSelector,
  onlyIfForm,
} from '../../../store.js'
import {isPresent, isNumeric} from '../../../common/utils.js'
import apiverson from '../../../common/apiverson.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import TextInput from '../../../common/components/TextInput.js'
import TextArea from '../../../common/components/TextArea.js'
import DatePicker from '../../../common/components/DatePicker.js'
import Select from '../../../common/components/Select.js'
import CurrencyInput from '../../../common/components/CurrencyInput.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {
  refreshStocktakeList,
  setupStocktakeListForm,
} from '../stocktakeListFunctions.js'
import {
  removeStocktake,
  setStocktake,
  stocktakeSelector,
} from '../../../data/stocktakes.js'
import {navigateStocktakeDetail} from '../../StocktakeDetailPage/stocktakeDetailFunctions.js'

export const MODAL_FORM = 'edit_stocktake_param_modal'

export function showStocktakeInternalNotesModal(referenceID) {
  const stocktake = stocktakeSelector(getState(), {referenceID})

  showEditStocktakeParamModal({
    referenceID,
    value: stocktake.internal_notes,
    display: 'Notes',
    apiProp: 'internal_notes',
    inputType: 'textarea',
  })
}

export function showStocktakeReferenceIDModal(referenceID) {
  const [, value] = referenceID.split(/^ST-/)

  showEditStocktakeParamModal({
    referenceID,
    value,
    display: 'Stocktake ID',
    apiProp: 'reference_id',
    inputType: 'text',
    needsStocktakeListRefresh: true,
    isRequired: true,
    typeCast(value) {
      return `ST-${value}`
    },
  })
}

export function defaultCast(value) {
  return value
}

export function dateCast(value) {
  return value ? value.toISOString() : null
}

export function numberCast(value) {
  return Number(value)
}

export function showEditStocktakeParamModal({
  referenceID,
  value,
  display,
  apiProp,
  isRequired,
  needsStocktakeListRefresh,
  inputType,
  typeCast,
  SubComponent,
  optionsSelector,
}) {
  inputType = inputType || 'text'

  setForm(MODAL_FORM, {
    referenceID,
    apiProp,
    value:
      inputType === 'date'
        ? value
          ? new Date(value)
          : null
        : isPresent(value)
          ? value
          : '',
    display,
    isRequired: !!isRequired,
    needsStocktakeListRefresh: !!needsStocktakeListRefresh,
    inputType,
    typeCast:
      typeCast ||
      (inputType === 'date'
        ? dateCast
        : inputType === 'currency'
          ? numberCast
          : defaultCast),
    isSaving: false,
    serverError: null,
    SubComponent,
    optionsSelector,
  })
}

export function updateModalForm(...args) {
  return updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  return removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export const errorsSelector = createSelector(
  modalFormSelector,
  ({value, display, isRequired, inputType}) => {
    const errors = {}

    if (isRequired && !isPresent(value)) {
      errors.value = `${display} is required`
      errors.preventSave = true
    }

    if (inputType === 'currency' && !isNumeric(value)) {
      errors.value = `${display} must be a number`
      errors.preventSave = true
    }

    return errors
  },
)

export async function saveStocktakeParam() {
  try {
    const {
      referenceID,
      value,
      display,
      apiProp,
      needsStocktakeListRefresh,
      typeCast,
    } = modalFormSelector(getState())

    const params = {
      [apiProp]: typeCast(value),
    }

    updateModalForm({isSaving: true, serverError: null})

    const {json} = await apiverson.put(`/stocktake/${referenceID}`, params)

    setStocktake(json)

    // we changed the referenceID so reload the detail page
    if (apiProp === 'reference_id' && referenceID !== json.reference_id) {
      removeStocktake(referenceID)

      // only navigates if on detail page
      navigateStocktakeDetail(undefined, json.reference_id)

      // reference ID might be on the stocktake list
      setupStocktakeListForm()
    }

    closeModal()

    showMessageToast(`Saved Stocktake ${display.toLowerCase()}.`)

    if (needsStocktakeListRefresh) {
      await refreshStocktakeList()
    }
  } catch (err) {
    updateModalForm({
      serverError: err.message || err.error_message,
      isSaving: false,
    })
  }
}

function EditStocktakeParamModal({form}) {
  const errors = useSelector(errorsSelector)
  const options = useSelector((state) =>
    form.optionsSelector ? form.optionsSelector(state) : [],
  )

  return (
    <ConfirmModal
      title={`Edit ${form.display}`}
      modalSize="sm"
      onConfirm={() => saveStocktakeParam()}
      onCancel={() => closeModal()}
      confirmText="Save"
      cancelText="Cancel"
      isSaving={form.isSaving}
      isDisabled={errors.preventSave}
      error={form.serverError}
      preventInnerScroll
    >
      <div className="list__item--form list__item--no-style margin-bottom-20">
        <div className="fs-01">
          <strong>{form.referenceID}</strong>
        </div>
      </div>
      {form.inputType === 'text' ? (
        <TextInput
          className="margin-bottom-0"
          value={form.value}
          onChange={(value) => updateModalForm({value})}
          errorMessage={errors.value}
          prefix={form.apiProp === 'reference_id' ? 'ST-' : null}
          autoFocus
        />
      ) : form.inputType === 'textarea' ? (
        <TextArea
          className="fs-00 lh-md margin-bottom-0"
          value={form.value}
          onChange={(value) => updateModalForm({value})}
          errorMessage={errors.value}
          autoFocus
        />
      ) : form.inputType === 'date' ? (
        <DatePicker
          id="stocktake_param_modal"
          selected={form.value}
          onChange={(value) =>
            updateModalForm({
              value,
            })
          }
        />
      ) : form.inputType === 'currency' ? (
        <CurrencyInput
          className="biggie margin-bottom-0"
          width="sm"
          value={form.value}
          onChange={(value) => updateModalForm({value})}
          errorMessage={errors.value}
          autoFocus
        />
      ) : form.inputType === 'select' ? (
        <Select
          value={form.value}
          onChange={(value) => updateModalForm({value})}
          errorMessage={errors.value}
          autoFocus
        >
          {options.map(({value, display}) => (
            <option key={value} value={value}>
              {display}
            </option>
          ))}
        </Select>
      ) : null}
      {form.SubComponent && <form.SubComponent form={form} />}
    </ConfirmModal>
  )
}

export default onlyIfForm(EditStocktakeParamModal, modalFormSelector)
